import React, { useEffect, useState } from "react";
import PropTypes from "kf-proptypes/widgets";
import { OverflowText } from "@widgets/datadisplay/overflowtext";

import {
  KfIcon,
  KF_ICONS,
  ICON_SIZE,
  ICON_STROKE
} from "@widgets/common/components/primitives/kf_icon/index.jsx";
import { Button, BUTTON_TYPES } from "../../button/index.jsx";
import { BaseDropdown } from "../base.jsx";
import {
  Badge,
  BADGE_COLORS,
  BADGE_SHAPES,
  BADGE_SIZES
} from "@widgets/datadisplay/index.jsx";

import styles from "./inlineDropdown.css";

export const INLINE_DROPDOWN_SIZES = {
  BASE: "base",
  MEDIUM: "medium"
};

InlineDropdown.propTypes = {
  /**
   * Different size of the dropdown
   */
  size: PropTypes.oneOf(Object.values(INLINE_DROPDOWN_SIZES)),
  /**
   * Icon to  display in left side
   */
  icon: PropTypes.element,
  /**
   * To customise the dropdown list
   */
  listRenderer: PropTypes.func,
  /**
   * Title for the inline dropdown
   */
  title: PropTypes.string,
  /**
   * Selected value for the dropdown
   */
  value: PropTypes.string.isRequired,
  /**
   * Default value for the dropdown
   */
  defaultValue: PropTypes.string,
  /**
   * Show red badge next to label
   */
  indicator: PropTypes.string,
  /**
   * Width of the dropdown
   */
  width: PropTypes.string,
  /**
   * Trigger when opening the dropdown
   */
  onOpen: PropTypes.func,
  /**
   * Trigger when close the dropdown
   */
  onClose: PropTypes.func,
  /**
   * Trigger when user focus out the inline dropdown
   */
  onBlur: PropTypes.func,
  /**
   * To override the styles
   */
  className: PropTypes.string,
  /**
   * Dropdown will be shown based on this prop
   */
  isOpen: PropTypes.bool.isRequired
};

InlineDropdown.defaultProps = {
  onOpen: () => {},
  onClose: () => {},
  onBlur: () => {},
  isOpen: false
};

/**
 * Use this component to show the Dropdown with inline list
 */

export function InlineDropdown(props) {
  const {
    icon,
    title,
    value,
    defaultValue,
    listRenderer,
    width,
    indicator,
    className = "",
    onOpen = () => {},
    onClose = () => {},
    onBlur = () => {},
    isOpen = false,
    size = INLINE_DROPDOWN_SIZES.BASE,
    ...restProps
  } = props;

  const [popupVisible, setPopupVisible] = useState(false);

  useEffect(
    function openEffect() {
      setPopupVisible(isOpen);
    },
    [isOpen]
  );

  function listRendererOnOpen() {
    setPopupVisible(true);
    onOpen();
  }

  function listRendererOnClose() {
    setPopupVisible(false);
    onClose();
  }

  function onBlurHandler(e) {
    e.preventDefault();
    setPopupVisible(false);
    onBlur();
  }
  return (
    <BaseDropdown
      onOpen={listRendererOnOpen}
      onClose={listRendererOnClose}
      isOpen={isOpen}
      targetRenderer={() => {
        return (
          <Button
            type={BUTTON_TYPES.ACTION}
            size={size}
            leftIcon={icon}
            rightIcon={
              <KfIcon
                icon={KF_ICONS.ARROW_DOWN_SIMPLE}
                stroke={ICON_STROKE.REGULAR}
                size={ICON_SIZE.SIZE_12}
                className={styles.arrowIcon}
              />
            }
            style={{ width: width ? width : "240px" }}
            className={`${className} ${styles.inlineButton} ${
              popupVisible ? styles.open : ""
            }`}
            onBlur={onBlurHandler}
          >
            <div
              className={`${styles.title} ${styles.titleIndicator} ${styles[size]}`}
            >
              {title}
              {indicator ? (
                <span className={styles.indicator}>
                  <Badge
                    color={BADGE_COLORS.RED}
                    shape={BADGE_SHAPES.DOT}
                    size={BADGE_SIZES.SMALL}
                    count={1}
                  />
                </span>
              ) : null}
            </div>

            <OverflowText className={`${styles.selectedValue} ${styles[size]}`}>
              {value || defaultValue}
            </OverflowText>
          </Button>
        );
      }}
      listRenderer={listRenderer}
      {...restProps}
    />
  );
}
